import { useState, useEffect } from 'react';
import i18n from '@/i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Positions équirectangulaires (viewBox 1000x420, lon -90..70, lat 30..-35)
const T = [
  { code: 'GUADELOUPE', label: 'Guadeloupe', x: 178, y: 89, anchor: 'end', dx: -16 },
  { code: 'MARTINIQUE', label: 'Martinique', x: 196, y: 118, anchor: 'start', dx: 16 },
  { code: 'GUYANE', label: 'Guyane', x: 231, y: 168, anchor: 'start', dx: 16 },
  { code: 'MAYOTTE', label: 'Mayotte', x: 845, y: 277, anchor: 'end', dx: -16 },
  { code: 'REUNION', label: 'Réunion', x: 910, y: 331, anchor: 'end', dx: -16 },
];

// Pays & territoires du monde (même projection : lon -90..70 → x 0..1000, lat 30..-35 → y 0..420)
const W = [
  { code: 'CUBA', label: '🇨🇺 Cuba', x: 69, y: 55, anchor: 'start', dx: 12 },
  { code: 'HAITI', label: '🇭🇹 Haïti', x: 111, y: 74, anchor: 'start', dx: 12 },
  { code: 'BRESIL', label: '🇧🇷 Brésil', x: 263, y: 296, anchor: 'start', dx: 12 },
  { code: 'MAROC', label: '🇲🇦 Maroc', x: 519, y: 26, anchor: 'end', dx: -12 },
  { code: 'FRANCE', label: '🇫🇷 France', x: 577, y: 26, anchor: 'start', dx: 12 },
  { code: 'SENEGAL', label: '🇸🇳 Sénégal', x: 473, y: 99, anchor: 'end', dx: -12 },
  { code: 'COTE-DIVOIRE', label: "🇨🇮 Côte d'Ivoire", x: 538, y: 145, anchor: 'start', dx: 12 },
  { code: 'AFRIQUE-DU-SUD', label: '🇿🇦 Afrique du Sud', x: 738, y: 372, anchor: 'end', dx: -12 },
  { code: 'MADAGASCAR', label: '🇲🇬 Madagascar', x: 856, y: 317, anchor: 'end', dx: -12 },
  { code: 'MAURICE', label: '🇲🇺 Maurice', x: 934, y: 372, anchor: 'end', dx: -12 },
];

const arc = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const my = Math.min(a.y, b.y) - Math.max(40, Math.abs(a.x - b.x) * 0.18);
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
};

// Carte interactive « réseau des Outre-mer » (accueil + catalogue) — `showAll` : pastille « Tous les territoires »
const BASSINS = [
  { key: 'ALL', label: '🌍 Monde', vb: '0 0 1000 420' },
  { key: 'CARAIBES', label: 'Caraïbes', vb: '20 20 330 330' },
  { key: 'AFRIQUE', label: 'Afrique · Europe', vb: '430 0 340 400' },
  { key: 'OCEAN_INDIEN', label: 'Océan Indien', vb: '770 220 230 200' },
];

export const TerritoryMap = ({ zone, onSelect, showAll = false }) => {
  const [stats, setStats] = useState({});
  const [bassin, setBassin] = useState('ALL');

  useEffect(() => {
    fetch(`${API_URL}/api/v2/catalog/zones-stats`)
      .then((r) => (r.ok ? r.json() : {}))
      .then(setStats)
      .catch(() => {});
  }, []);

  const vb = BASSINS.find((b) => b.key === bassin)?.vb || '0 0 1000 420';
  const k = Math.sqrt(Number(vb.split(' ')[2]) / 1000);

  return (
  <div className="relative rounded-3xl overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border border-white/[0.08] mb-6" data-testid="territory-map"
    style={{ background: 'radial-gradient(120% 140% at 50% -20%, rgba(217,179,90,0.10), rgba(20,8,38,0.4) 45%, rgba(12,4,24,0.6))' }}>
    <div className="min-w-[560px] sm:min-w-0" style={{ aspectRatio: '1000 / 420' }}>
    <svg viewBox={vb} preserveAspectRatio="xMidYMid meet" className="w-full h-full block" role="img" aria-label="Carte des territoires d'Outre-mer et pays du monde">
      <defs>
        <linearGradient id="tm-arc" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#D9B35A" stopOpacity="0.05" />
          <stop offset="50%" stopColor="#D9B35A" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#D9B35A" stopOpacity="0.05" />
        </linearGradient>
        <radialGradient id="tm-glow">
          <stop offset="0%" stopColor="#D9B35A" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#D9B35A" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Grille méridiens / parallèles */}
      {[...Array(9)].map((_, i) => (
        <line key={`v${i}`} x1={100 + i * 100} y1="0" x2={100 + i * 100} y2="420" stroke="rgba(255,255,255,0.045)" strokeWidth="1" />
      ))}
      {[...Array(4)].map((_, i) => (
        <line key={`h${i}`} x1="0" y1={84 + i * 84} x2="1000" y2={84 + i * 84} stroke="rgba(255,255,255,0.045)" strokeWidth="1" />
      ))}
      {/* Équateur */}
      <line x1="0" y1="194" x2="1000" y2="194" stroke="rgba(217,179,90,0.18)" strokeWidth="1" strokeDasharray="6 8" />
      <text x="12" y="188" fontSize="11" fill="rgba(217,179,90,0.4)" fontStyle="italic">Équateur</text>

      {/* Arcs du réseau coopératif */}
      {[[0, 1], [1, 2], [2, 3], [3, 4], [0, 4]].map(([a, b]) => (
        <path key={`${a}-${b}`} d={arc(T[a], T[b])} fill="none" stroke="url(#tm-arc)" strokeWidth="1.5" strokeDasharray="4 6">
          <animate attributeName="stroke-dashoffset" from="60" to="0" dur="6s" repeatCount="indefinite" />
        </path>
      ))}

      {/* Étiquettes de bassins */}
      {bassin === 'ALL' && (
        <>
          <text x="200" y="38" fontSize="13" fill="rgba(247,242,233,0.45)" textAnchor="middle" letterSpacing="3">ANTILLES · GUYANE</text>
          <text x="855" y="38" fontSize="13" fill="rgba(247,242,233,0.45)" textAnchor="middle" letterSpacing="3">OCÉAN INDIEN</text>
          <text x="530" y="410" fontSize="12" fill="rgba(140,198,62,0.45)" textAnchor="middle" letterSpacing="3">PAYS & TERRITOIRES PARTENAIRES DU MONDE</text>
        </>
      )}

      {/* Pays du monde */}
      {W.map((t) => {
        const active = zone === t.code;
        return (
          <g key={t.code} onClick={() => onSelect(t.code)} style={{ cursor: 'pointer' }} data-testid={`map-zone-${t.code}`}>
            {active && <circle cx={t.x} cy={t.y} r={26 * k} fill="url(#tm-glow)" />}
            <circle cx={t.x} cy={t.y} r={(active ? 11 : 8) * k} fill={active ? 'rgba(140,198,62,0.28)' : 'rgba(255,255,255,0.05)'}
              stroke={active ? '#8CC63E' : 'rgba(140,198,62,0.45)'} strokeWidth={active ? 2 : 1}
              style={{ transition: 'all .25s ease' }} />
            <circle cx={t.x} cy={t.y} r={(active ? 4.5 : 3) * k} fill={active ? '#B6E27A' : '#8CC63E'} style={{ transition: 'all .25s ease' }} />
            {active && (
              <circle cx={t.x} cy={t.y} r={11 * k} fill="none" stroke="#8CC63E" strokeWidth="1.5" opacity="0.8">
                <animate attributeName="r" values={`${11 * k};${24 * k}`} dur="1.8s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.7;0" dur="1.8s" repeatCount="indefinite" />
              </circle>
            )}
            <text x={t.x + t.dx * k} y={t.y + 4 * k} fontSize={(active ? 13 : 11.5) * k} fontWeight={active ? 700 : 500}
              fill={active ? '#B6E27A' : 'rgba(247,242,233,0.55)'} textAnchor={t.anchor}
              style={{ transition: 'all .25s ease', userSelect: 'none' }}>
              {t.label}
            </text>
            {stats[t.code] && (
              <text x={t.x + t.dx * k} y={t.y + 19 * k} fontSize={10 * k} fontWeight="500"
                fill={active ? 'rgba(140,198,62,0.95)' : 'rgba(140,198,62,0.6)'} textAnchor={t.anchor}
                data-testid={`map-stats-${t.code}`} style={{ userSelect: 'none' }}>
                                            {[stats[t.code].products > 0 ? stats[t.code].products + ' ' + i18n.t('landing.map_products', 'produits') : null, stats[t.code].members > 0 ? stats[t.code].members + ' ' + i18n.t('landing.map_adherents', 'adhérents') : null].filter(Boolean).join(' · ')}
              </text>
            )}
          </g>
        );
      })}

      {/* Marqueurs */}
      {T.map((t) => {
        const active = zone === t.code;
        return (
          <g key={t.code} onClick={() => onSelect(t.code)} style={{ cursor: 'pointer' }} data-testid={`map-zone-${t.code}`}>
            {active && <circle cx={t.x} cy={t.y} r={34 * k} fill="url(#tm-glow)" />}
            <circle cx={t.x} cy={t.y} r={(active ? 15 : 11) * k} fill={active ? 'rgba(217,179,90,0.28)' : 'rgba(255,255,255,0.06)'}
              stroke={active ? '#D9B35A' : 'rgba(217,179,90,0.45)'} strokeWidth={active ? 2 : 1.2}
              style={{ transition: 'all .25s ease' }} />
            <circle cx={t.x} cy={t.y} r={(active ? 5.5 : 4) * k} fill={active ? '#E9CF8E' : '#D9B35A'} style={{ transition: 'all .25s ease' }}>
              {!active && <animate attributeName="opacity" values="1;0.45;1" dur="2.4s" repeatCount="indefinite" />}
            </circle>
            {active && (
              <circle cx={t.x} cy={t.y} r={15 * k} fill="none" stroke="#D9B35A" strokeWidth="1.5" opacity="0.8">
                <animate attributeName="r" values={`${15 * k};${30 * k}`} dur="1.8s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.7;0" dur="1.8s" repeatCount="indefinite" />
              </circle>
            )}
            <text x={t.x + t.dx * k} y={t.y + 5 * k} fontSize={(active ? 16 : 14) * k} fontWeight={active ? 700 : 500}
              fill={active ? '#E9CF8E' : 'rgba(247,242,233,0.75)'} textAnchor={t.anchor}
              style={{ transition: 'all .25s ease', userSelect: 'none' }}>
              {t.label}
            </text>
            {stats[t.code] && (
              <text x={t.x + t.dx * k} y={t.y + 22 * k} fontSize={10.5 * k} fontWeight="500"
                fill={active ? 'rgba(217,179,90,0.95)' : 'rgba(217,179,90,0.6)'} textAnchor={t.anchor}
                data-testid={`map-stats-${t.code}`} style={{ userSelect: 'none' }}>
                                {[stats[t.code].products > 0 ? stats[t.code].products + ' ' + i18n.t('landing.map_produits', 'produits') : null, stats[t.code].members > 0 ? stats[t.code].members + ' ' + i18n.t('landing.map_adherents', 'adhérents') : null].filter(Boolean).join(' · ')}
              </text>
            )}
          </g>
        );
      })}
    </svg>
    </div>
    <p className="absolute bottom-2 right-4 text-[10px]" style={{ color: 'rgba(247,242,233,0.35)' }}>
      {i18n.t('landing.map_hint', 'Cliquez sur un territoire pour découvrir ses produits')}
    </p>
    <div className="absolute bottom-2 left-4 flex items-center gap-3 text-[10px]" data-testid="map-legend" style={{ color: 'rgba(247,242,233,0.45)' }}>
      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: '#D9B35A' }} /> Outre-mer O'SCOP</span>
      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: '#8CC63E' }} /> Pays & territoires du monde</span>
    </div>
    <div className="absolute top-3 right-3 hidden sm:flex flex-wrap gap-1.5" data-testid="map-bassins">
      {BASSINS.map((b) => (
        <button key={b.key} type="button" onClick={() => setBassin(b.key)} data-testid={`map-bassin-${b.key}`}
          className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${bassin === b.key
            ? 'bg-[#D9B35A] text-black border-[#D9B35A]'
            : 'bg-black/40 text-white/70 border-white/20 hover:bg-black/60 backdrop-blur-sm'}`}>
          {b.label}
        </button>
      ))}
    </div>
    {showAll && (
      <button type="button" onClick={() => onSelect('')} data-testid="map-zone-all"
        className={`absolute top-3 left-3 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${!zone
          ? 'bg-[#D9B35A] text-black border-[#D9B35A]'
          : 'bg-black/40 text-white/75 border-[#D9B35A]/40 hover:bg-black/60 backdrop-blur-sm'}`}>
        🌍 {i18n.t('landing.map_tous', 'Tous les territoires')}
      </button>
    )}
  </div>
  );
};
